import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";
import {
  type TimeSeriesViewerOptions,
  DEFAULT_TIME_SERIES_VIEWER_OPTIONS,
} from "@models/Dataset";
import {
  DEFAULT_VISUALIZATION_BASE,
  type VisualizationBase,
} from "@models/VisualizationBase";
import type { WidgetRollup } from "@models/Rollup";
import type { MetricBase, MetricPopulation, MetricRepresentedResult } from "@models/Metric";

// Time unit types for line chart
export type TimeUnit = "quarter" | "semester" | "month" | "week";

export type CategoryRollupScope = "default" | "variable" | "value";

export interface CategoryRollupsByScope {
  default?: WidgetRollup[] | null;
  variable?: WidgetRollup[] | null;
  value?: WidgetRollup[] | null;
}

export function getCategoryRollupScope(
  focusMode?: string | null,
): CategoryRollupScope {
  const lower = focusMode?.toLowerCase();
  if (lower === "variable") return "variable";
  if (lower === "value") return "value";
  return "default";
}

// Breakout focus mode for Single questions with multiple variables
export type BreakoutFocusMode = "VARIABLE" | "VALUE";

// Breakout configuration
export interface BreakoutConfig {
  enabled: boolean;
  sourceLineId: string | null;
  sourceLineLabel?: string;
  questionId: string | null;
  questionLabel?: string;
  questionTitle?: string;
  questionType?: "Single" | "Multiple";
  variableCount?: number;
  valueCount?: number;
  // Focus mode: only relevant for Single questions with multiple variables
  focusMode: BreakoutFocusMode | null;
  focusVariableId?: string; // selected variable datasetElementId for Variable Focus
  focusValueId?: string; // selected value datasetElementId for Value Focus
  crossFocusEntity?: string; // resolved API value: variable.label (VARIABLE) or value.value (VALUE)
}

export interface BreakoutQuestionOption {
  id: string; // datasetElementId
  label: string;
  title: string;
  type: "Single" | "Multiple";
  variableCount: number;
  valueCount: number;
}

// Per-line weight configuration (similar to crosstab per-segment weights)
export interface PerLineWeight {
  surveyId: string;
  configurationId: string;
  datasetElementQuestionId: string;
  datasetElementVariableId: string;
  weightVariableLabel: string;
}

// Focus mode for Single questions with multiple variables
export type LineFocusMode = "variable" | "value";

export interface LineFocusSelection {
  mode: LineFocusMode;
  selectedVariableId?: string;
  selectedValueId?: string;
}

// Line visibility control
export interface LineVisibility {
  [lineId: string]: boolean;
}

// Sorting option for line order
export type LineSortingOption = "original" | "manual";

// Statistical testing types
export type StatTestType = "z-test" | "t-test";
export type SignificanceLevel = "90%" | "95%" | "99%";
export type StatMarkerStyle = "arrows" | "circles";
export type StatTestingTimeStrategy = "PREVIOUS_PERIOD" | "YEAR_OVER_YEAR";

// Y-Axis range mode
export type YAxisRangeMode = "auto" | "custom";

export interface StatisticalTesting {
  enabled: boolean;
  testType: StatTestType;
  significanceLevel: SignificanceLevel;
  minimumSampleSize: number;
  markerStyle?: StatMarkerStyle;
  timeStrategy?: StatTestingTimeStrategy;
}

export interface RollingAverage {
  enabled: boolean;
  windowSize: number; // 2, 3, 4, 6, 8, 12 depending on timeUnit
}

// Rolling average configuration
export interface RollingAverage {
  enabled: boolean;
  windowSize: number; // Number of periods to include (2, 3, 4, 6, 8, 12, etc.)
}

// Line chart series data point
export interface LineDataPoint {
  x: number | string; // timestamp or period label
  y: number | null; // null for suppressed or missing data
  statSignificance?: number; // -1, 0, 1 for arrows
  baseN?: number; // Sample size for this data point
  missingRollingPeriodLabels?: string[]; // 0-based offsets of missing periods within rolling window
  priorYearLabel?: string | null; // YoY: prior-year period label from backend; null = no equivalent exists
  priorYearValue?: number | null; // YoY: value of the prior-year period
  priorYearInRange?: boolean; // YoY: computed — true if priorYearLabel exists in the visible categories
}

// Line chart series
export interface LineChartSeries {
  id: string;
  name: string;
  data: LineDataPoint[];
  color?: string;
  visible?: boolean;
}

// Line chart configuration
export interface LineChartConfig {
  // Metadata
  datasetId?: string;
  datasetName?: string;
  questionId?: string;
  datasetVersionId?: number | null;

  // Question info
  questionLabel?: string;
  questionName?: string;
  questionType?: "Single" | "Multiple" | "Float" | "Number";

  // Time configuration
  timeUnit: TimeUnit;
  startDate: string | null; // ISO date string
  endDate: string | null; // ISO date string
  endDateMode: "fixed" | "latest_available";

  // Date range constraints from API
  responsesMinDate?: string; // ISO date string (YYYY-MM-DD)
  responsesMaxDate?: string; // ISO date string (YYYY-MM-DD)
  dataAvailabilityDate?: string | null; // ISO date string (YYYY-MM-DD)

  // Rolling average metadata from API response
  effectiveRollingPeriodCount?: number; // Actual periods used (for partial windows)
  rollingPeriodsWarningMessage?: string; // Warning message if insufficient data
  comparisonRollingLabel?: string; // Formatted string of periods used
  missingRollingPeriodLabels?: string[];

  // Focus mode (for Single questions with multiple variables)
  focusSelection: LineFocusSelection | null;

  // Focus parameters for API request (stores the actual label/value strings)
  focusMode?: string; // "VARIABLE" or "VALUE" (uppercase for API)
  focusEntity?: string; // variable.label or value.value
  focusEntityDisplayLabel?: string; // Display label as shown in dropdown (rowtitle/title for variables, title/label for values)

  // Rollup focus (mutually exclusive with focusMode/focusEntity)
  focusRollup?: WidgetRollup; // sent to API
  focusRollupName?: string; // display-only, configJson only

  // Category-dimension rollups keyed by focus scope — merges several lines into one on save/search
  categoryRollupsByScope?: CategoryRollupsByScope | null;

  // Persistent map of lineId (= variableId) → line label, accumulated across all fetches.
  // Never cleared so source lines absorbed by rollups can still be resolved to display names.
  lineIdLabelMap?: Record<string, string>;

  // Weight
  weight: "DISABLED" | "DATASET_DEFAULT" | "PER_LINE_WEIGHT";

  // Per-line weights (keyed by lineId from series.id)
  perLineWeights: Record<string, PerLineWeight>;

  // Filter toggle
  applyFilters: boolean;

  // Statistical testing
  statisticalTesting?: StatisticalTesting;

  // Rolling average
  rollingAverage?: RollingAverage;

  // Presentation
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;
  showMarkers: boolean;
  colorPalette: string; // "Orange" | "Green" | "Blue" | "Yellow" | "Multicolor" | "Custom"
  customColors: Record<string, string>; // lineId → hex, only used when colorPalette === "Custom"
  showLegend: boolean;

  // Line visibility
  lineVisibility: LineVisibility;

  // Line customization
  customLineLabels: Record<string, string>; // lineId -> custom label
  lineOrder: string[]; // Array of line IDs in display order
  sortingOption: LineSortingOption; // "original" or "manual"

  // Y-Axis Range settings
  yAxisRangeMode: YAxisRangeMode; // "auto" (default) or "custom"
  yAxisMin?: number; // Min value when custom
  yAxisMax?: number; // Max value when custom

  // Rounding (decimal places)
  percentageFormatting: number; // 0, 1, or 2 decimal places

  // Viewer Time Options
  viewerOptions: TimeSeriesViewerOptions;

  // Visualization Base (researcher-only) — restricts the eligible respondent pool.
  // Optional so older saved configs without it default to "All eligible respondents".
  visualizationBase?: VisualizationBase;

  // Chart Source (Dataset Metrics in Line Chart PRD-67) — absent means
  // "question", same as every line chart saved before this field existed.
  // "metric" means this chart's series come from a saved DatasetMetric
  // rather than researcher-picked Focus Selection/Base; see datasetMetric*
  // below. Mirrors BarChartConfig's fields exactly (chart-config-slice.ts).
  chartSourceType?: "question" | "metric";
  /** Stable reference to the saved DatasetMetric.id this chart was built from. */
  datasetMetricId?: string | null;
  /** Snapshot of the metric's name at selection/save time — used to detect
   *  whether Chart title was renamed away from the metric's default, and as
   *  a display fallback if the metric can no longer be resolved. */
  datasetMetricName?: string | null;
  /** Snapshot of the metric's own saved Base/Metric population at selection
   *  time — read-only display in the editor; drives visualizationBase above. */
  datasetMetricBase?: MetricBase | null;
  datasetMetricPopulation?: MetricPopulation | null;
  /** The metric's own represented results as of the last selection/refresh —
   *  used to narrow every refetch's response down to exactly the metric's
   *  lines (see filterFetchedDataToMetricResults). */
  datasetMetricRepresentedResults?: MetricRepresentedResult[] | null;

  // Breakout configuration
  breakout: BreakoutConfig;

  // Chart data
  series: LineChartSeries[];
  categories: string[]; // Time period labels

  // Footer summary data
  footerSummary: {
    periodic: string; // e.g., "Quarter"
    period: string; // e.g., "07/01/2024 - 09/30/2025"
    weight: string; // e.g., "Default Weight" or "No Weight"
    rollingAverageDescription?: string; // e.g., "Each point shows the average of the current quarter plus 1 preceding quarter"
    rollingAverageNote?: string; // e.g., "ℹ️ Note: The first 1 data point will show partial rolling averages..."
  };
}

interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  responses: number;
}

interface LineChartConfigState {
  currentConfig: LineChartConfig;
  savedConfigs: LineChartConfig[];
  question: Questions | null;
  datasetId: string | null;
  datasetName: string | null;
  questionId: string | null;
  datasetVersionId: number | null;
  activeFilters: any[]; // QuestionResponsesSearchFilter[]
}

const initialConfig: LineChartConfig = {
  timeUnit: "quarter",
  startDate: null,
  endDate: null,
  endDateMode: "fixed",
  focusSelection: null,
  weight: "DISABLED",
  perLineWeights: {},
  applyFilters: false,
  title: "",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  showMarkers: true,
  colorPalette: "Multicolor",
  customColors: {},
  showLegend: true,
  lineVisibility: {},
  customLineLabels: {},
  lineOrder: [],
  sortingOption: "original",
  yAxisRangeMode: "auto",
  yAxisMin: undefined,
  yAxisMax: undefined,
  percentageFormatting: 0,
  series: [],
  categories: [],
  rollingAverage: {
    enabled: false,
    windowSize: 2,
  },
  viewerOptions: DEFAULT_TIME_SERIES_VIEWER_OPTIONS,
  visualizationBase: { ...DEFAULT_VISUALIZATION_BASE },
  chartSourceType: undefined,
  datasetMetricId: undefined,
  datasetMetricName: undefined,
  datasetMetricBase: undefined,
  datasetMetricPopulation: undefined,
  datasetMetricRepresentedResults: undefined,
  breakout: {
    enabled: false,
    sourceLineId: null,
    questionId: null,
    focusMode: null,
  },
  footerSummary: {
    periodic: "Quarter",
    period: "",
    weight: "No Weight",
    rollingAverageDescription: undefined,
    rollingAverageNote: undefined,
  },
};

const initialState: LineChartConfigState = {
  currentConfig: initialConfig,
  savedConfigs: [],
  question: null,
  datasetId: null,
  datasetName: null,
  questionId: null,
  datasetVersionId: null,
  activeFilters: [],
};

// Helper function to update period label in footer
function updatePeriodLabel(state: LineChartConfigState) {
  const { startDate, endDate } = state.currentConfig;

  if (startDate && endDate) {
    const formatDate = (dateStr: string) => {
      // Parse the date string as UTC to avoid timezone conversion issues
      // ISO date format is YYYY-MM-DD
      const [year, month, day] = dateStr.split("-");
      return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
    };

    state.currentConfig.footerSummary.period = `${formatDate(startDate)} - ${formatDate(endDate)}`;
  } else {
    state.currentConfig.footerSummary.period = "";
  }
}

const HEX_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function sanitizeCustomColors(
  raw: Record<string, string>,
): Record<string, string> {
  const clean: Record<string, string> = {};
  for (const [id, hex] of Object.entries(raw)) {
    if (HEX_RE.test(hex.trim())) {
      clean[id] = hex.trim().toUpperCase();
    }
    // Invalid hex silently dropped — line falls back to palette color
  }
  return clean;
}

const lineChartConfigSlice = createSlice({
  name: "lineChartConfig",
  initialState,
  reducers: {
    // Metadata updates
    updateChartMetadata: (
      state,
      action: PayloadAction<{
        datasetId: string;
        datasetName?: string;
        questionId: string;
        datasetVersionId?: number | null;
        activeFilters?: any[];
      }>,
    ) => {
      state.datasetId = action.payload.datasetId;
      state.datasetName = action.payload.datasetName ?? null;
      state.questionId = action.payload.questionId;
      state.datasetVersionId = action.payload.datasetVersionId ?? null;
      state.activeFilters = action.payload.activeFilters ?? [];
      state.currentConfig.datasetId = action.payload.datasetId;
      state.currentConfig.datasetName = action.payload.datasetName;
      state.currentConfig.questionId = action.payload.questionId;
      state.currentConfig.datasetVersionId = action.payload.datasetVersionId;
    },

    // Metric-sourced: keep the root/currentConfig questionId in sync with the
    // metric's live source question, which is editable independently in
    // Dataset Configuration after this chart was created/saved — see
    // use-line-metric-source-revalidation.ts. Narrower than
    // updateChartMetadata on purpose: doesn't touch datasetId/datasetName/
    // datasetVersionId/activeFilters, which must stay as they were.
    updateResolvedQuestionId: (state, action: PayloadAction<string>) => {
      state.questionId = action.payload;
      state.currentConfig.questionId = action.payload;
    },

    // Question updates
    updateQuestions: (
      state,
      action: PayloadAction<{
        id: string;
        label: string;
        name: string;
        type?: "Single" | "Multiple" | "Float" | "Number";
        responses?: number;
      }>,
    ) => {
      state.question = {
        id: action.payload.id,
        label: action.payload.label,
        name: action.payload.name,
        responses: action.payload.responses || 0,
      };
      state.currentConfig.questionId = action.payload.id;
      state.currentConfig.questionLabel = action.payload.label;
      state.currentConfig.questionName = action.payload.name;
      state.currentConfig.questionType = action.payload.type;

      // Auto-set title to question name if not manually edited
      if (!state.currentConfig.title) {
        state.currentConfig.title = action.payload.name;
      }
    },

    setDataAvailabilityDate: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.currentConfig.dataAvailabilityDate = action.payload;
    },

    // Time configuration
    updateTimeUnit: (state, action: PayloadAction<TimeUnit>) => {
      state.currentConfig.timeUnit = action.payload;

      // Update footer summary
      const unitLabels: Record<TimeUnit, string> = {
        quarter: "Quarter",
        semester: "Semester",
        month: "Month",
        week: "Week",
      };
      state.currentConfig.footerSummary.periodic = unitLabels[action.payload];
    },

    updateStartDate: (state, action: PayloadAction<string | null>) => {
      state.currentConfig.startDate = action.payload;
      updatePeriodLabel(state);
    },

    updateEndDate: (state, action: PayloadAction<string | null>) => {
      state.currentConfig.endDate = action.payload;
      updatePeriodLabel(state);
    },

    updateEndDateMode: (
      state,
      action: PayloadAction<"fixed" | "latest_available">,
    ) => {
      state.currentConfig.endDateMode = action.payload;
    },

    // Focus mode (for Single questions with multiple variables)
    updateFocusSelection: (
      state,
      action: PayloadAction<LineFocusSelection | null>,
    ) => {
      state.currentConfig.focusSelection = action.payload;
    },

    // Update focus parameters for API request
    updateFocusParams: (
      state,
      action: PayloadAction<{
        focusMode: string;
        focusEntity: string;
        focusEntityDisplayLabel?: string;
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.focusMode = action.payload.focusMode;
        state.currentConfig.focusEntity = action.payload.focusEntity;
        state.currentConfig.focusEntityDisplayLabel =
          action.payload.focusEntityDisplayLabel;
      } else {
        state.currentConfig.focusMode = undefined;
        state.currentConfig.focusEntity = undefined;
        state.currentConfig.focusEntityDisplayLabel = undefined;
      }
    },

    // Update rollup focus for API request (mutually exclusive with focusEntity)
    updateFocusRollup: (
      state,
      action: PayloadAction<{
        rollup: WidgetRollup;
        name?: string;
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.focusRollup = action.payload.rollup;
        state.currentConfig.focusRollupName = action.payload.name;
      } else {
        state.currentConfig.focusRollup = undefined;
        state.currentConfig.focusRollupName = undefined;
      }
    },

    // Category-dimension rollups (merge several lines into one on save/search)
    updateCategoryRollups: (
      state,
      action: PayloadAction<WidgetRollup[] | null>,
    ) => {
      const scope = getCategoryRollupScope(state.currentConfig.focusMode);
      if (!state.currentConfig.categoryRollupsByScope) {
        state.currentConfig.categoryRollupsByScope = {};
      }
      state.currentConfig.categoryRollupsByScope[scope] = action.payload;
    },
    addCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      const scope = getCategoryRollupScope(state.currentConfig.focusMode);
      if (!state.currentConfig.categoryRollupsByScope) {
        state.currentConfig.categoryRollupsByScope = {};
      }
      if (!state.currentConfig.categoryRollupsByScope[scope]) {
        state.currentConfig.categoryRollupsByScope[scope] = [];
      }
      state.currentConfig.categoryRollupsByScope[scope]!.push(action.payload);
    },
    updateCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      const scope = getCategoryRollupScope(state.currentConfig.focusMode);
      const rollups = state.currentConfig.categoryRollupsByScope?.[scope];
      if (!rollups) return;
      const idx = rollups.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) {
        rollups[idx] = action.payload;
      }
    },
    removeCategoryRollup: (state, action: PayloadAction<string>) => {
      const scope = getCategoryRollupScope(state.currentConfig.focusMode);
      const rollups = state.currentConfig.categoryRollupsByScope?.[scope];
      if (!rollups) return;
      state.currentConfig.categoryRollupsByScope![scope] = rollups.filter(
        (r) => r.id !== action.payload,
      );
    },

    // Weight
    updateWeight: (
      state,
      action: PayloadAction<"DISABLED" | "DATASET_DEFAULT" | "PER_LINE_WEIGHT">,
    ) => {
      // If changing away from per-line weighting, clear per-line weights
      if (
        state.currentConfig.weight === "PER_LINE_WEIGHT" &&
        action.payload !== "PER_LINE_WEIGHT"
      ) {
        state.currentConfig.perLineWeights = {};
      }

      state.currentConfig.weight = action.payload;

      // Update footer summary
      if (action.payload === "DISABLED") {
        state.currentConfig.footerSummary.weight = "No Weight";
      } else if (action.payload === "PER_LINE_WEIGHT") {
        state.currentConfig.footerSummary.weight = "Per-Line Weighting";
      } else {
        state.currentConfig.footerSummary.weight = "Default Weight";
      }
    },

    // Per-line weights
    updatePerLineWeights: (
      state,
      action: PayloadAction<Record<string, PerLineWeight>>,
    ) => {
      state.currentConfig.perLineWeights = action.payload;
    },

    // Filter toggle
    updateApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.applyFilters = action.payload;
    },

    // Presentation
    updateTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },

    updateSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },

    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },

    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },

    updateShowMarkers: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showMarkers = action.payload;
    },

    updateColorPalette: (state, action: PayloadAction<string>) => {
      state.currentConfig.colorPalette = action.payload;
    },

    updateCustomColor: (
      state,
      action: PayloadAction<{ lineId: string; color: string }>,
    ) => {
      state.currentConfig.customColors[action.payload.lineId] =
        action.payload.color;
    },

    setAllCustomColors: (
      state,
      action: PayloadAction<{ lineIds: string[]; color: string }>,
    ) => {
      const { lineIds, color } = action.payload;
      lineIds.forEach((lineId) => {
        state.currentConfig.customColors[lineId] = color;
      });
    },

    updateShowLegend: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showLegend = action.payload;
    },

    // Line visibility
    updateLineVisibility: (
      state,
      action: PayloadAction<{ lineId: string; visible: boolean }>,
    ) => {
      state.currentConfig.lineVisibility[action.payload.lineId] =
        action.payload.visible;
    },

    toggleAllLines: (state, action: PayloadAction<boolean>) => {
      const visibility: LineVisibility = {};
      state.currentConfig.series.forEach((series) => {
        visibility[series.id] = action.payload;
      });
      state.currentConfig.lineVisibility = visibility;
    },

    // Chart data
    updateChartData: (
      state,
      action: PayloadAction<{
        series: LineChartSeries[];
        categories: string[];
        responsesMinDate?: string;
        responsesMaxDate?: string;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      state.currentConfig.series = action.payload.series;
      state.currentConfig.categories = action.payload.categories;

      // Accumulate lineId → line label entries. Merge rather than replace so
      // that source lines absorbed by rollups remain resolvable in later fetches.
      if (!state.currentConfig.lineIdLabelMap) {
        state.currentConfig.lineIdLabelMap = {};
      }
      for (const s of action.payload.series) {
        state.currentConfig.lineIdLabelMap[s.id] = s.name;
      }

      // Drop customColors entries for lines no longer in the dataset.
      // Skipped while breakout is active so original line colors are preserved
      // in the map and restored automatically when breakout is disabled.
      if (!state.currentConfig.breakout?.enabled) {
        const incomingIds = new Set(action.payload.series.map((s) => s.id));
        Object.keys(state.currentConfig.customColors).forEach((id) => {
          if (!incomingIds.has(id)) {
            delete state.currentConfig.customColors[id];
          }
        });
      }

      // Update date range constraints if provided
      if (action.payload.responsesMinDate) {
        state.currentConfig.responsesMinDate = action.payload.responsesMinDate;

        // Auto-set startDate if not already set
        if (!state.currentConfig.startDate) {
          state.currentConfig.startDate = action.payload.responsesMinDate;
        }
      }
      if (action.payload.responsesMaxDate) {
        state.currentConfig.responsesMaxDate = action.payload.responsesMaxDate;

        // Auto-set endDate if not already set
        if (!state.currentConfig.endDate) {
          state.currentConfig.endDate = action.payload.responsesMaxDate;
        }
      }

      // Update rolling average metadata if provided
      if (action.payload.effectiveRollingPeriodCount !== undefined) {
        state.currentConfig.effectiveRollingPeriodCount =
          action.payload.effectiveRollingPeriodCount;
      }
      if (action.payload.rollingPeriodsWarningMessage !== undefined) {
        state.currentConfig.rollingPeriodsWarningMessage =
          action.payload.rollingPeriodsWarningMessage;
      }
      if (action.payload.comparisonRollingLabel !== undefined) {
        state.currentConfig.comparisonRollingLabel =
          action.payload.comparisonRollingLabel;
      }
      if (action.payload.missingRollingPeriodLabels !== undefined) {
        state.currentConfig.missingRollingPeriodLabels =
          action.payload.missingRollingPeriodLabels;
      }
      // Update period label in footer after setting dates
      updatePeriodLabel(state);

      // Initialize line visibility for new series
      action.payload.series.forEach((series) => {
        if (!(series.id in state.currentConfig.lineVisibility)) {
          state.currentConfig.lineVisibility[series.id] = true;
        }
      });

      // Initialize line order if empty or if new series are added
      const currentLineIds = new Set(state.currentConfig.lineOrder || []);
      const newLineIds = action.payload.series.map((s) => s.id);

      // If lineOrder is empty or doesn't match current series, reset it
      if (
        !state.currentConfig.lineOrder ||
        state.currentConfig.lineOrder.length === 0 ||
        newLineIds.some((id) => !currentLineIds.has(id))
      ) {
        state.currentConfig.lineOrder = newLineIds;
      }
    },

    // Statistical testing
    updateStatTestingEnabled: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        // Enable: create or update the statisticalTesting object
        if (!state.currentConfig.statisticalTesting) {
          state.currentConfig.statisticalTesting = {
            enabled: true,
            testType: "t-test",
            significanceLevel: "90%",
            minimumSampleSize: 30,
          };
        } else {
          state.currentConfig.statisticalTesting.enabled = true;
        }
        // Circle markers conflict visually with regular marker dots
        if (state.currentConfig.statisticalTesting.markerStyle === "circles") {
          state.currentConfig.showMarkers = false;
        }
      } else {
        // Disable: remove the entire statisticalTesting object to ensure clean state
        state.currentConfig.statisticalTesting = undefined;
      }
    },

    updateStatTestType: (state, action: PayloadAction<StatTestType>) => {
      if (state.currentConfig.statisticalTesting) {
        state.currentConfig.statisticalTesting.testType = action.payload;
      }
    },

    updateSignificanceLevel: (
      state,
      action: PayloadAction<SignificanceLevel>,
    ) => {
      if (state.currentConfig.statisticalTesting) {
        state.currentConfig.statisticalTesting.significanceLevel =
          action.payload;
      }
    },

    updateMinimumSampleSize: (state, action: PayloadAction<number>) => {
      if (state.currentConfig.statisticalTesting) {
        state.currentConfig.statisticalTesting.minimumSampleSize =
          action.payload;
      }
    },

    updateStatMarkerStyle: (
      state,
      action: PayloadAction<StatMarkerStyle>,
    ) => {
      if (state.currentConfig.statisticalTesting) {
        state.currentConfig.statisticalTesting.markerStyle = action.payload;
        // Circle markers conflict visually with regular marker dots
        if (action.payload === "circles") {
          state.currentConfig.showMarkers = false;
        }
      }
    },

    updateStatTestingTimeStrategy: (
      state,
      action: PayloadAction<StatTestingTimeStrategy>,
    ) => {
      if (state.currentConfig.statisticalTesting) {
        state.currentConfig.statisticalTesting.timeStrategy = action.payload;
      }
    },

    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = {
          enabled: action.payload,
          windowSize: 2,
        };
      } else {
        state.currentConfig.rollingAverage.enabled = action.payload;
      }
    },

    updateRollingAverageWindowSize: (state, action: PayloadAction<number>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = {
          enabled: false,
          windowSize: action.payload,
        };
      } else {
        state.currentConfig.rollingAverage.windowSize = action.payload;
      }
    },

    // Breakout configuration
    updateBreakoutEnabled: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        // Enabling breakout: reset to fresh state
        state.currentConfig.breakout = {
          enabled: true,
          sourceLineId: null,
          questionId: null,
          focusMode: null,
        };

        // Clear per-line weights when breakout is enabled
        // Line IDs change from original (e.g., hScores_3r1) to broken-out format (e.g., hScores_3r1_1)
        // But keep the weight mode so user can configure weights for the new broken-out lines
        state.currentConfig.perLineWeights = {};
      } else {
        // Disabling: reset all breakout state and restore line visibility
        state.currentConfig.breakout = {
          enabled: false,
          sourceLineId: null,
          questionId: null,
          focusMode: null,
          focusVariableId: undefined,
          focusValueId: undefined,
        };
        // Restore all original lines to visible
        const visibility: LineVisibility = {};
        state.currentConfig.series.forEach((s) => {
          visibility[s.id] = true;
        });
        state.currentConfig.lineVisibility = visibility;

        // Clear per-line weights when breakout is disabled (per PRD-30)
        state.currentConfig.perLineWeights = {};
        // Custom colors for breakout-generated IDs are discarded lazily:
        // updateChartData removes any ID not present in the incoming series.

        // Revert weight mode to default unless user changed it manually
        if (state.currentConfig.weight === "PER_LINE_WEIGHT") {
          state.currentConfig.weight = "DATASET_DEFAULT";
          state.currentConfig.footerSummary.weight = "Default Weight";
        }
      }
    },

    updateBreakoutSourceLine: (
      state,
      action: PayloadAction<{ id: string; label: string } | null>,
    ) => {
      state.currentConfig.breakout.sourceLineId = action.payload?.id ?? null;
      state.currentConfig.breakout.sourceLineLabel = action.payload?.label;

      // Clear per-line weights when source line changes
      // The broken-out line IDs will be different (e.g., hScores_3r1_1 vs hScores_3r2_1)
      state.currentConfig.perLineWeights = {};
    },

    updateBreakoutQuestion: (
      state,
      action: PayloadAction<BreakoutQuestionOption | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.breakout.questionId = action.payload.id;
        state.currentConfig.breakout.questionLabel = action.payload.label;
        state.currentConfig.breakout.questionTitle = action.payload.title;
        state.currentConfig.breakout.questionType = action.payload.type;
        state.currentConfig.breakout.variableCount =
          action.payload.variableCount;
        state.currentConfig.breakout.valueCount = action.payload.valueCount;
        // Reset focus mode when question changes
        state.currentConfig.breakout.focusMode = null;
        state.currentConfig.breakout.focusVariableId = undefined;
        state.currentConfig.breakout.focusValueId = undefined;
        state.currentConfig.breakout.crossFocusEntity = undefined;

        // Clear per-line weights when breakout question changes (per PRD-30)
        state.currentConfig.perLineWeights = {};

        // Reset weight mode to default when breakout question changes
        if (state.currentConfig.weight === "PER_LINE_WEIGHT") {
          state.currentConfig.weight = "DATASET_DEFAULT";
          state.currentConfig.footerSummary.weight = "Default Weight";
        }
      } else {
        state.currentConfig.breakout.questionId = null;
        state.currentConfig.breakout.questionLabel = undefined;
        state.currentConfig.breakout.questionTitle = undefined;
        state.currentConfig.breakout.questionType = undefined;
        state.currentConfig.breakout.variableCount = undefined;
        state.currentConfig.breakout.valueCount = undefined;
        state.currentConfig.breakout.focusMode = null;
        state.currentConfig.breakout.focusVariableId = undefined;
        state.currentConfig.breakout.focusValueId = undefined;
        state.currentConfig.breakout.crossFocusEntity = undefined;
      }
    },

    updateBreakoutFocusMode: (
      state,
      action: PayloadAction<BreakoutFocusMode | null>,
    ) => {
      console.log("🎯 [BREAKOUT-FOCUS-MODE] Changing focus mode:", {
        from: state.currentConfig.breakout.focusMode,
        to: action.payload,
        clearingWeights: state.currentConfig.perLineWeights,
      });

      state.currentConfig.breakout.focusMode = action.payload;
      // Reset focus entity when mode changes
      state.currentConfig.breakout.focusVariableId = undefined;
      state.currentConfig.breakout.focusValueId = undefined;
      state.currentConfig.breakout.crossFocusEntity = undefined;

      // Clear per-line weights when focus mode changes
      // Focus mode change completely replaces the set of lines shown
      // (e.g., from "all values of variable X" to "all variables with value Y")
      state.currentConfig.perLineWeights = {};
    },

    updateBreakoutFocusVariable: (
      state,
      action: PayloadAction<{
        id: string | undefined;
        entityLabel: string | undefined;
      }>,
    ) => {
      state.currentConfig.breakout.focusVariableId = action.payload.id;
      state.currentConfig.breakout.crossFocusEntity =
        action.payload.entityLabel;

      // Clear per-line weights when focus variable changes
      // Different variable = different set of broken-out lines
      state.currentConfig.perLineWeights = {};
    },

    updateBreakoutFocusValue: (
      state,
      action: PayloadAction<{
        id: string | undefined;
        entityValue: string | undefined;
      }>,
    ) => {
      state.currentConfig.breakout.focusValueId = action.payload.id;
      state.currentConfig.breakout.crossFocusEntity =
        action.payload.entityValue;

      // Clear per-line weights when focus value changes
      // Different value = different set of broken-out lines
      state.currentConfig.perLineWeights = {};
    },

    updateViewerOptions: (state, action: PayloadAction<TimeSeriesViewerOptions>) => {
      state.currentConfig.viewerOptions = action.payload;
    },

    updateVisualizationBase: (
      state,
      action: PayloadAction<VisualizationBase>,
    ) => {
      state.currentConfig.visualizationBase = action.payload;
    },

    updateDatasetMetricSource: (
      state,
      action: PayloadAction<{
        datasetMetricId: string;
        datasetMetricName: string;
        base: MetricBase;
        population: MetricPopulation;
        representedResults?: MetricRepresentedResult[];
      } | null>,
    ) => {
      if (action.payload) {
        state.currentConfig.chartSourceType = "metric";
        state.currentConfig.datasetMetricId = action.payload.datasetMetricId;
        state.currentConfig.datasetMetricName = action.payload.datasetMetricName;
        state.currentConfig.datasetMetricBase = action.payload.base;
        state.currentConfig.datasetMetricPopulation = action.payload.population;
        if (action.payload.representedResults) {
          state.currentConfig.datasetMetricRepresentedResults =
            action.payload.representedResults;
        }
      } else {
        state.currentConfig.chartSourceType = "question";
        state.currentConfig.datasetMetricId = null;
        state.currentConfig.datasetMetricName = null;
        state.currentConfig.datasetMetricBase = null;
        state.currentConfig.datasetMetricPopulation = null;
        state.currentConfig.datasetMetricRepresentedResults = null;
      }
    },

    updateFooterSummary: (
      state,
      action: PayloadAction<Partial<typeof state.currentConfig.footerSummary>>,
    ) => {
      state.currentConfig.footerSummary = {
        ...state.currentConfig.footerSummary,
        ...action.payload,
      };
    },

    // Update only response date constraints and rolling average fields (without touching data)
    updateResponseDates: (
      state,
      action: PayloadAction<{
        responsesMinDate?: string;
        responsesMaxDate?: string;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      if (action.payload.responsesMinDate) {
        state.currentConfig.responsesMinDate = action.payload.responsesMinDate;
      }
      if (action.payload.responsesMaxDate) {
        state.currentConfig.responsesMaxDate = action.payload.responsesMaxDate;
      }
      // Update rolling average response fields
      if (action.payload.effectiveRollingPeriodCount !== undefined) {
        state.currentConfig.effectiveRollingPeriodCount =
          action.payload.effectiveRollingPeriodCount;
      }
      if (action.payload.rollingPeriodsWarningMessage !== undefined) {
        state.currentConfig.rollingPeriodsWarningMessage =
          action.payload.rollingPeriodsWarningMessage;
      }
      if (action.payload.comparisonRollingLabel !== undefined) {
        state.currentConfig.comparisonRollingLabel =
          action.payload.comparisonRollingLabel;
      }
      if (action.payload.missingRollingPeriodLabels !== undefined) {
        state.currentConfig.missingRollingPeriodLabels =
          action.payload.missingRollingPeriodLabels;
      }
    },

    // Save and load
    saveConfig: (state, action: PayloadAction<string>) => {
      const configToSave = {
        ...state.currentConfig,
        id: action.payload,
      };
      state.savedConfigs.push(configToSave);
    },

    loadConfig: (state, action: PayloadAction<LineChartConfig>) => {
      state.currentConfig = {
        ...action.payload,
        // Existing configs without endDateMode are treated as "fixed" (preserves current behavior)
        endDateMode: action.payload.endDateMode ?? "fixed",
        rollingAverage: action.payload.rollingAverage ?? {
          enabled: false,
          windowSize: 2,
        },
        breakout: action.payload.breakout ?? {
          enabled: false,
          sourceLineId: null,
          questionId: null,
          focusMode: null,
        },
        perLineWeights: action.payload.perLineWeights ?? {},
        viewerOptions:
          action.payload.viewerOptions ?? DEFAULT_TIME_SERIES_VIEWER_OPTIONS,
        visualizationBase:
          action.payload.visualizationBase ?? { ...DEFAULT_VISUALIZATION_BASE },
        // Explicit undefined/"question" default — a leftover Metric
        // reference from a previously-edited widget must be cleared here
        // rather than left to fall through (mirrors chart-config-slice.ts).
        chartSourceType: action.payload.chartSourceType ?? "question",
        datasetMetricId: action.payload.datasetMetricId ?? null,
        datasetMetricName: action.payload.datasetMetricName ?? null,
        datasetMetricBase: action.payload.datasetMetricBase ?? null,
        datasetMetricPopulation: action.payload.datasetMetricPopulation ?? null,
        datasetMetricRepresentedResults:
          action.payload.datasetMetricRepresentedResults ?? null,
      customColors: sanitizeCustomColors(action.payload.customColors ?? {}),
      };
    },

    // Y-Axis Range
    updateYAxisRangeMode: (state, action: PayloadAction<YAxisRangeMode>) => {
      state.currentConfig.yAxisRangeMode = action.payload;
      // Clear custom values when switching to auto
      if (action.payload === "auto") {
        state.currentConfig.yAxisMin = undefined;
        state.currentConfig.yAxisMax = undefined;
      }
    },

    updateYAxisMin: (state, action: PayloadAction<number | undefined>) => {
      state.currentConfig.yAxisMin = action.payload;
    },

    updateYAxisMax: (state, action: PayloadAction<number | undefined>) => {
      state.currentConfig.yAxisMax = action.payload;
    },

    // Rounding
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },

    // Line customization
    updateCustomLineLabel: (
      state,
      action: PayloadAction<{ lineId: string; label: string }>,
    ) => {
      state.currentConfig.customLineLabels[action.payload.lineId] =
        action.payload.label;
    },

    resetCustomLineLabel: (state, action: PayloadAction<string>) => {
      delete state.currentConfig.customLineLabels[action.payload];
    },

    reorderLines: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const newOrder = [...state.currentConfig.lineOrder];
      const [removed] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, removed);
      state.currentConfig.lineOrder = newOrder;
    },

    updateSortingOption: (state, action: PayloadAction<LineSortingOption>) => {
      state.currentConfig.sortingOption = action.payload;

      // If switching to "original", reset the order to match series order
      if (action.payload === "original") {
        state.currentConfig.lineOrder = state.currentConfig.series.map(
          (s) => s.id,
        );
      }
    },

    // Reset
    resetToDefault: (state) => {
      state.currentConfig = { ...initialConfig };
    },
  },
});

export const {
  updateChartMetadata,
  updateResolvedQuestionId,
  updateQuestions,
  setDataAvailabilityDate,
  updateTimeUnit,
  updateStartDate,
  updateEndDate,
  updateEndDateMode,
  updateFocusSelection,
  updateFocusParams,
  updateFocusRollup,
  updateCategoryRollups,
  addCategoryRollup,
  updateCategoryRollup,
  removeCategoryRollup,
  updateWeight,
  updatePerLineWeights,
  updateApplyFilters,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
  updateStatMarkerStyle,
  updateStatTestingTimeStrategy,
  updateTitle,
  updateSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updateShowMarkers,
  updateColorPalette,
  updateShowLegend,
  updateLineVisibility,
  toggleAllLines,
  updateChartData,
  updateResponseDates,
  saveConfig,
  loadConfig,
  resetToDefault,
  updateYAxisRangeMode,
  updateYAxisMin,
  updateYAxisMax,
  updatePercentageFormatting,
  updateCustomLineLabel,
  resetCustomLineLabel,
  reorderLines,
  updateSortingOption,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateBreakoutEnabled,
  updateBreakoutSourceLine,
  updateBreakoutQuestion,
  updateBreakoutFocusMode,
  updateBreakoutFocusVariable,
  updateBreakoutFocusValue,
  updateViewerOptions,
  updateVisualizationBase,
  updateDatasetMetricSource,
  updateCustomColor,
  setAllCustomColors,
} = lineChartConfigSlice.actions;

export const lineChartConfigReducer = lineChartConfigSlice.reducer;

export const lineChartConfigSelector = (state: RootState) =>
  state.lineChartConfig;

export const lineChartConfigDispatcher = createDispatchers(
  lineChartConfigSlice.actions,
);

export default lineChartConfigSlice.reducer;
